/* ============================================================
 * 积分模块 Pinia store（C 端）
 *
 * 设计：
 *   - 全部走 fail-soft：失败把 `errorMessage` / `aigcBalanceError` 设进去，
 *     不抛错；调用方可继续渲染旧 state。聚合（summary）尤其需要，避免网络
 *     抖动造成 UI 跳变。
 *   - 流水 loadTx(reset=true) 重置分页；否则累加（pageNum 自增）。
 *   - startRecharge 成功后**直接返回** order，不写 `currentOrder`——
 *     currentOrder 由支付页拿到返回值后调 `setCurrentOrder` 写入。
 *   - `loadAigcBalance(force)`：30s 内存缓存（避免页面频繁抖动），失败
 *     把 `aigcBalanceError` 设为非空，不 throw，调用方可继续渲染降级态。
 *   - 错误文案统一走 `src/utils/error.ts: getErrorMessage`，ApiError.message
 *     直接拿到后端文案。
 * ============================================================ */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  createPointsRecharge,
  getAigcBalance,
  getPointsOrder,
  getPointsPlans,
  getPointsSummary,
  listPointsTx,
} from '@/api/points'
import type {
  AigcBalanceVO,
  PointsOrder,
  PointsPaymentMethod,
  PointsPlan,
  PointsPlanKey,
  PointsSummary,
  PointsTx,
  PointsTxType,
} from '@/types/points'
import { getErrorMessage } from '@/utils/error'

const DEFAULT_TX_PAGE_SIZE = 10
const AIGC_BALANCE_CACHE_TTL_MS = 30_000

export const usePointsStore = defineStore('points', () => {
  const summary = ref<PointsSummary | null>(null)
  const txs = ref<PointsTx[]>([])
  const plans = ref<PointsPlan[]>([])
  const currentOrder = ref<PointsOrder | null>(null)

  const loadingSummary = ref(false)
  const loadingTx = ref(false)
  const loadingPlans = ref(false)
  const creatingOrder = ref(false)
  const refreshingOrder = ref(false)

  const txTotal = ref(0)
  const txPageNum = ref(1)
  const txPageSize = ref(DEFAULT_TX_PAGE_SIZE)
  const txFilter = ref<PointsTxType | ''>('')
  const txHasMore = computed(() => txs.value.length < txTotal.value)

  const aigcBalance = ref<AigcBalanceVO | null>(null)
  const aigcBalanceLoading = ref(false)
  const aigcBalanceError = ref<string | null>(null)
  let aigcBalanceLoadedAt = 0

  const errorMessage = ref('')

  async function loadSummary(force = false): Promise<void> {
    if (loadingSummary.value) return
    if (!force && summary.value) return
    loadingSummary.value = true
    errorMessage.value = ''
    try {
      const resp = await getPointsSummary()
      summary.value = resp.data
    } catch (err) {
      errorMessage.value = getErrorMessage(err, '加载积分账户失败')
    } finally {
      loadingSummary.value = false
    }
  }

  async function loadAigcBalance(force = false): Promise<void> {
    if (aigcBalanceLoading.value) return
    if (!force && aigcBalance.value && Date.now() - aigcBalanceLoadedAt < AIGC_BALANCE_CACHE_TTL_MS) {
      return
    }
    aigcBalanceLoading.value = true
    aigcBalanceError.value = null
    try {
      const resp = await getAigcBalance()
      aigcBalance.value = resp.data
      aigcBalanceLoadedAt = Date.now()
    } catch (err) {
      aigcBalanceError.value = getErrorMessage(err, '获取协同 AIGC 额度失败')
    } finally {
      aigcBalanceLoading.value = false
    }
  }

  async function loadTx(reset = false): Promise<void> {
    if (loadingTx.value) return
    if (reset) {
      txPageNum.value = 1
      txs.value = []
    }
    loadingTx.value = true
    errorMessage.value = ''
    try {
      const resp = await listPointsTx({
        txType: txFilter.value,
        pageNum: txPageNum.value,
        pageSize: txPageSize.value,
      })
      txs.value = [...txs.value, ...resp.rows]
      txTotal.value = resp.total
      txPageNum.value += 1
    } catch (err) {
      errorMessage.value = getErrorMessage(err, '加载积分流水失败')
    } finally {
      loadingTx.value = false
    }
  }

  async function loadPlans(force = false): Promise<void> {
    if (loadingPlans.value) return
    if (!force && plans.value.length > 0) return
    loadingPlans.value = true
    errorMessage.value = ''
    try {
      const resp = await getPointsPlans()
      plans.value = resp.data
    } catch (err) {
      errorMessage.value = getErrorMessage(err, '加载充值套餐失败')
    } finally {
      loadingPlans.value = false
    }
  }

  async function startRecharge(
    planKey: PointsPlanKey,
    opts: { paymentMethod?: PointsPaymentMethod; merchantKey?: string } = {},
  ): Promise<PointsOrder | null> {
    if (creatingOrder.value) return null
    creatingOrder.value = true
    errorMessage.value = ''
    try {
      const resp = await createPointsRecharge({ planKey, ...opts })
      return resp.data
    } catch (err) {
      errorMessage.value = getErrorMessage(err, '充值下单失败')
      return null
    } finally {
      creatingOrder.value = false
    }
  }

  async function refreshOrder(orderNo: string): Promise<PointsOrder | null> {
    if (refreshingOrder.value) return currentOrder.value
    refreshingOrder.value = true
    errorMessage.value = ''
    try {
      const resp = await getPointsOrder(orderNo)
      currentOrder.value = resp.data
      return resp.data
    } catch (err) {
      errorMessage.value = getErrorMessage(err, '查询订单状态失败')
      return null
    } finally {
      refreshingOrder.value = false
    }
  }

  function setCurrentOrder(order: PointsOrder | null): void {
    currentOrder.value = order
  }

  function setTxFilter(value: PointsTxType | ''): void {
    if (txFilter.value === value) return
    txFilter.value = value
    txPageNum.value = 1
    txs.value = []
    txTotal.value = 0
  }

  function reset(): void {
    summary.value = null
    txs.value = []
    plans.value = []
    currentOrder.value = null
    loadingSummary.value = false
    loadingTx.value = false
    loadingPlans.value = false
    creatingOrder.value = false
    refreshingOrder.value = false
    txTotal.value = 0
    txPageNum.value = 1
    txPageSize.value = DEFAULT_TX_PAGE_SIZE
    txFilter.value = ''
    aigcBalance.value = null
    aigcBalanceLoading.value = false
    aigcBalanceError.value = null
    aigcBalanceLoadedAt = 0
    errorMessage.value = ''
  }

  return {
    summary,
    txs,
    plans,
    currentOrder,
    loadingSummary,
    loadingTx,
    loadingPlans,
    creatingOrder,
    refreshingOrder,
    txTotal,
    txPageNum,
    txPageSize,
    txFilter,
    txHasMore,
    aigcBalance,
    aigcBalanceLoading,
    aigcBalanceError,
    errorMessage,
    loadSummary,
    loadAigcBalance,
    loadTx,
    loadPlans,
    startRecharge,
    refreshOrder,
    setCurrentOrder,
    setTxFilter,
    reset,
  }
})